import React, {useState, useEffect, useCallback, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody, Table, Loading} from '../../../components'
import {CustomCell} from '../../../components/Table/columns/CustomCell'
import {CustomCellDouble} from '../../../components/Table/columns/CustomCellDouble'
import {CustomHeader} from '../../../components/Table/columns/CustomHeader'
import {ListHeader} from '../../../components/Table/header/ListHeader'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {
  subcontractorDocumentSearch,
  subcontractorDocumentExcel,
} from '../../../redux/slices/subcontractordocument.slice'
import {allCompanyLookup, projectLookup, basicLookup} from '../../../redux/slices/lookup.slice'

const selectSubcontractorDocumentData = (state) => ({
  list: state.subcontractorDocument.list,
  loading: state.subcontractorDocument.loading,
})

const selectLookupData = (state) => ({
  projectLookupList: state.lookup.projectLookupList,
  companyLookup: state.lookup.companyLookup,
  SubcontractorDocumentState: state.lookup.SubcontractorDocumentState,
})

const stateBadgeClass = (stateId) => {
  switch (stateId) {
    case 1:
      return 'badge badge-light-warning'
    case 2:
      return 'badge badge-light-danger'
    case 3:
      return 'badge badge-light-success'
    default:
      return 'badge badge-light'
  }
}

function List() {
  const intl = useIntl()
  const dispatch = useDispatch()
  const pageKey = 'SUBCONTRACTORDOCUMENT'

  const {list, loading} = useSelector(selectSubcontractorDocumentData)
  const {projectLookupList, companyLookup, SubcontractorDocumentState} =
    useSelector(selectLookupData)

  const [params, setParams] = useState()

  useEffect(() => {
    if (params) dispatch(subcontractorDocumentSearch(params))
  }, [dispatch, params])

  useEffect(() => {
    dispatch(allCompanyLookup())
    dispatch(projectLookup())
    dispatch(basicLookup('SubcontractorDocumentState'))
  }, [dispatch])

  const handleSorting = useCallback((item) => {
    setParams((prevState) => ({...prevState, sort: item && item.sort, order: item && item.order}))
  }, [])

  const handleSearch = useCallback((item) => {
    setParams((prevState) => ({...prevState, name: item}))
  }, [])

  const handlePagination = useCallback((pagination) => {
    setParams((prevState) => ({...prevState, ...pagination}))
  }, [])

  const handleFilterSubmit = useCallback((values) => {
    setParams({...values})
  }, [])

  const handleExcelExport = useCallback(() => {
    if (params) {
      dispatch(
        subcontractorDocumentExcel({
          params,
          fileName: intl.formatMessage({id: 'BC.SUBCONTRACTORDOCUMENT'}),
        })
      )
    }
  }, [dispatch, params, intl])

  const months = useMemo(
    () =>
      Array.from({length: 12}, (_, i) => ({
        id: i + 1,
        name: intl.formatDate(new Date(2000, i, 1), {month: 'long'}),
      })),
    [intl]
  )

  const columns = useMemo(
    () => [
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.SEARCH.COMPANYTITLE'})}
            className='min-w-125px'
          />
        ),
        accessor: 'companyTitle',
        Cell: ({...props}) => <CustomCell item={props} />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.SEARCH.PROJECTNAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'projectName',
        Cell: ({...props}) => <CustomCell item={props} />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.SEARCH.PERIOD'})}
            className='min-w-125px'
          />
        ),
        accessor: 'year',
        Cell: ({...props}) => (
          <CustomCell
            item={props}
            customValue={`${props.data[props.row.index].year} / ${intl.formatDate(
              new Date(2000, (props.data[props.row.index].month || 1) - 1, 1),
              {month: 'long'}
            )}`}
          />
        ),
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.SEARCH.STATE'})}
            className='min-w-125px'
          />
        ),
        accessor: 'subcontractorDocumentStateName',
        Cell: ({...props}) => (
          <div className='p-5'>
            <span className={stateBadgeClass(props.data[props.row.index].subcontractorDocumentStateId)}>
              {props.cell.value}
            </span>
          </div>
        ),
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'LIST.LASTMODIFIEDINFO'})}
            className='min-w-125px'
          />
        ),
        accessor: 'lastModifiedBy',
        Cell: ({...props}) => <CustomCellDouble item={props} double='lastModifiedDate' />,
      },
    ],
    [intl, handleSorting]
  )

  const actionButtons = useMemo(
    () => [
      {
        title: 'BUTTON.EXCEL',
        onClick: handleExcelExport,
        icon: '/images/icons/duotune/arrows/arr044.svg',
        variant: 'light-primary',
      },
      {
        title: 'FILTER',
        icon: '/images/icons/duotune/general/gen031.svg',
        variant: 'light-primary',
        list: [
          {
            title: 'SUBCONTRACTORDOCUMENT.SEARCH.COMPANYTITLE',
            data: companyLookup,
            name: 'companyId',
          },
          {
            title: 'SUBCONTRACTORDOCUMENT.SEARCH.PROJECTNAME',
            data: projectLookupList,
            name: 'projectId',
          },
          {
            title: 'SUBCONTRACTORDOCUMENT.SEARCH.YEAR',
            name: 'year',
            type: 'text',
          },
          {
            title: 'SUBCONTRACTORDOCUMENT.SEARCH.MONTH',
            data: months,
            name: 'month',
          },
          {
            title: 'SUBCONTRACTORDOCUMENT.SEARCH.STATE',
            data: SubcontractorDocumentState,
            name: 'subcontractorDocumentStateId',
          },
        ],
      },
    ],
    [companyLookup, projectLookupList, months, SubcontractorDocumentState, handleExcelExport]
  )

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'BC.SUBCONTRACTORDOCUMENT'})}</PageTitle>
      <KTCard>
        <ListHeader
          search={handleSearch}
          actionButtons={actionButtons}
          onSubmit={handleFilterSubmit}
          pages={pageKey}
        />
        <KTCardBody>
          {!list && <ListLoading />}
          {list && (
            <Table
              items={list}
              cols={columns}
              handlePagination={handlePagination}
              paddless
              pages={pageKey}
            />
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {List}
